<script setup>
import AppTopBar from '@/components/app/AppTopBar.vue'
import AppNavBar from '@/components/app/AppNavBar.vue'
import WidthLimiter from '@/components/shared/page/WidthLimiter.vue'
import {usePage} from '@/composables/use-page.js'
import {toRefs, ref, watchEffect} from 'vue'

//Emits and props
defineEmits([
  'back',
])
const props = defineProps({
  header: {
    type: String,
    default: '',
  },
  canGoBack: {
    type: Boolean,
    default: false,
  },
  isFixedHeight: {
    type: Boolean,
    default: false,
  },
  isTransparent: {
    type: Boolean,
    default: false,
  },
})

//Refs and composables
const {header} = toRefs(props)
const {setTitle} = usePage()
const main = ref(null)

//Expose main
defineExpose({
  main,
})

//Watch header and set as page title
watchEffect(() => setTitle(header.value))
</script>

<template>
  <div :class="$style.wrapper">
    <AppTopBar>
      <template #actions>
        <slot name="actions" />
      </template>
    </AppTopBar>

    <main
      ref="main"
      :class="$style.main"
    >
      <AppNavBar
        v-if="!!header || $slots.header"
        :header="header"
        :can-go-back="canGoBack"
        :is-floating="isTransparent"
        @back="$emit('back', $event)"
      >
        <template #header>
          <slot name="header" />
        </template>
      </AppNavBar>

      <WidthLimiter
        :class="{
          [$style.contents]: true,
          [$style.hasHeader]: !!header || $slots.header,
          [$style.fixedHeight]: isFixedHeight,
          [$style.transparent]: isTransparent,
        }"
      >
        <slot />
      </WidthLimiter>
    </main>
  </div>
</template>

<style module lang="scss">
.wrapper {
  height: 100vh;
  width: 100vw;
  min-width: var(--breakpoint-xxxs);
  max-width: 100vw;
  display: flex;
  flex-direction: column;
}
.main {
  position: relative;
  height: calc(100vh - var(--topbar-height));
  width: 100%;
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  @media (min-width: 1024px) {
    overflow: auto;
    padding-bottom: 2rem;
  }
}
.contents {
  max-width: 100vw;
  flex: 1;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  overflow: auto;
  background: var(--bg-page-element);
  &.fixedHeight {
    height: calc(100% - 5.5rem);
  }
  @media (min-width: 1024px) {
    overflow: visible;
    padding: 1rem 1.5rem;
    border-radius: var(--border-radius-m);
    border: 1px solid var(--color-borders);
    width: calc(1024px - 2rem);
    &.transparent {
      background: transparent;
      border: 0;
      padding: 1rem;
      width: 100%;
    }
    &.hasHeader {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
      border-top: 0;
    }
  }
}
</style>
